/**
 * Zod validation for TemplateConfig. Guards template import, project settings
 * persistence, and API payloads against malformed / malicious config.
 */
import { z } from 'zod';
const hex = z
    .string()
    .regex(/^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$|^rgba?\([^)]*\)$/, 'invalid color');
export const animationSpecSchema = z.object({
    kind: z.enum([
        'none',
        'fade',
        'scale',
        'pop',
        'slideUp',
        'slideDown',
        'blurIn',
        'bounce',
        'trackingExpand',
        // Looping active-word motions (play continuously while a word is spoken).
        'wiggle',
        'jelly',
        'glow',
    ]),
    durationMs: z.number().min(0).max(5000),
    delayMs: z.number().min(0).max(5000),
    easing: z.enum(['linear', 'easeOut', 'easeInOut', 'spring']),
    intensity: z.number().min(0).max(3),
});
export const strokeSchema = z.object({ color: hex, widthPx: z.number().min(0).max(40) });
export const shadowSchema = z.object({
    color: z.string(),
    blurPx: z.number().min(0).max(80),
    offsetXPx: z.number().min(-40).max(40),
    offsetYPx: z.number().min(-40).max(40),
});
export const backgroundSchema = z.object({
    color: z.string().optional(),
    radiusPx: z.number().min(0).max(200),
    paddingXPx: z.number().min(0).max(200),
    paddingYPx: z.number().min(0).max(200),
    mode: z.enum(['none', 'block', 'perLine', 'perWord', 'activeWord']),
});
export const activeWordSchema = z.object({
    color: hex,
    scale: z.number().min(0.5).max(3),
    highlightColor: z.string().optional(),
    karaoke: z.boolean(),
    animation: animationSpecSchema,
});
export const emphasisSchema = z.object({
    color: hex.optional(),
    scale: z.number().min(0.5).max(3),
    highlightColor: z.string().optional(),
    bold: z.boolean(),
    /**
     * Put the emphasized/key word on its OWN new line, below the rest of the
     * caption, and skip the color highlight (size + weight only). The
     * "keyword drops to the next line, bigger, same color" short-form look.
     */
    newLine: z.boolean().optional(),
});
export const gradientSchema = z.object({
    from: z.string(),
    to: z.string(),
    angleDeg: z.number().min(-360).max(360),
});
export const extrudeSchema = z.object({
    color: z.string(),
    depthPx: z.number().min(0).max(60),
    dxPx: z.number().min(-10).max(10),
    dyPx: z.number().min(-10).max(10),
});
export const captionStyleSchema = z.object({
    fontFamily: z.string().min(1).max(80),
    fontWeight: z.number().min(100).max(1000),
    fontSizePx: z.number().min(8).max(400),
    textTransform: z.enum(['none', 'uppercase', 'lowercase', 'capitalize']),
    lineHeight: z.number().min(0.6).max(3),
    letterSpacingPx: z.number().min(-20).max(40),
    textAlign: z.enum(['left', 'center', 'right']),
    color: z.string(),
    spokenColor: z.string().optional(),
    stroke: strokeSchema.optional(),
    shadow: shadowSchema.optional(),
    background: backgroundSchema,
    gradient: gradientSchema.optional(),
    extrude: extrudeSchema.optional(),
    activeWord: activeWordSchema,
    activeWordHighlight: z.boolean().optional(),
    activeWordMotion: z.boolean().optional(),
    emphasis: emphasisSchema,
    entrance: animationSpecSchema,
    exit: animationSpecSchema,
});
export const placementSchema = z.object({
    anchor: z.enum(['top', 'upper', 'center', 'lower', 'bottom']),
    xNorm: z.number().min(0).max(1),
    yNorm: z.number().min(0).max(1),
    maxWidthNorm: z.number().min(0.1).max(1),
});
export const segmentationSchema = z.object({
    maxWords: z.number().int().min(1).max(20),
    preferredWords: z.number().int().min(1).max(20),
    maxChars: z.number().int().min(6).max(120),
    maxLines: z.number().int().min(1).max(4),
    pauseThresholdMs: z.number().min(0).max(5000),
    breakOnSentencePunctuation: z.boolean(),
});
export const templateConfigSchema = z.object({
    id: z.string().min(1).max(64),
    version: z.number().int().min(1),
    name: z.string().min(1).max(80),
    category: z.enum([
        'clean',
        'bold',
        'active-word',
        'karaoke',
        'pop',
        'subtitle-bar',
        'minimal',
        'outline',
        'highlight',
        'editorial',
        'podcast',
        'neon',
        'retro',
        'multiline',
        'kinetic',
        'depth',
        '3d',
    ]),
    description: z.string().max(240),
    style: captionStyleSchema,
    placement: placementSchema,
    segmentation: segmentationSchema,
    renderMode: z.enum(['overlay', 'behind']).optional(),
    ai: z.boolean().optional(),
});
/** Validate an unknown value as a TemplateConfig, throwing on failure. */
export function parseTemplateConfig(value) {
    return templateConfigSchema.parse(value);
}
export function safeParseTemplateConfig(value) {
    return templateConfigSchema.safeParse(value);
}
//# sourceMappingURL=schema.js.map